import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle, AlertTriangle, Pencil, Check, X, MapPin, XCircle, ChevronDown,
} from 'lucide-react';

// ─── Apufunktiot ──────────────────────────────────────────────────────────────

const LOCALE_MAP = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE', no: 'nb-NO', et: 'et-EE' };
function getLocale(lang) { return LOCALE_MAP[lang] || 'fi-FI'; }

// Hälytysviestin sensorikohtaiset otsikko- ja yksikkötaulukot — peilaa tarkasti
// backendin alertService.js:n buildMessage()-funktiota (rivi ~306). Backend
// tallentaa message-kentän valmiiksi renderöitynä suomeksi (audit-/lokitarkoitukseen,
// ks. spec Epic 6.3), mutta UI rakentaa näytettävän viestin aina uudelleen
// sensor_type/level/value-kentistä i18n:n kautta — sama data toimii siis
// kaikilla kielillä eikä vain uusille hälytyksille, myös jo tallennetuille.
const ALERT_SENSOR_LABEL_KEYS = {
  co: 'sensors.co', o2: 'sensors.o2', co2: 'sensors.co2',
  humidity: 'sensors.humidity', door: 'sensors.door',
  power: 'alerts.messageLabels.power', connection: 'alerts.messageLabels.connection',
};
const ALERT_SENSOR_UNITS = { co: 'ppm', o2: 'Vol-%', co2: 'ppm', humidity: '%RH', power: '', connection: 'min', door: '' };

// Rivillä näytetään vain tapahtuma, ei tasoa ("Kriittinen" poistettu näkyvistä
// 25.9.2026 — sivu on jo Hälytykset-sivu). Power-arvo on aina 0, joten sitä ei näytetä.
function buildAlertMessage(alert, t) {
  if (!alert.sensor_type) return alert.message;
  const labelKey = ALERT_SENSOR_LABEL_KEYS[alert.sensor_type];
  const sensor = labelKey ? t(labelKey) : alert.sensor_type.toUpperCase();
  if (alert.sensor_type === 'power') return sensor;
  if (alert.sensor_type === 'connection') return alert.value != null ? `${sensor} (${alert.value} min)` : sensor;
  const unit = ALERT_SENSOR_UNITS[alert.sensor_type] ?? '';
  return `${sensor}: ${alert.value}${unit ? ` ${unit}` : ''}`;
}

function formatTime(ts, locale) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(locale, {
    day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Kesto muodossa "5 vrk 18 h" / "27 h 5 min" / "12 min"
function formatDuration(ms, t) {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return t('alerts.duration.days', { d, h });
  if (h > 0) return t('alerts.duration.hours', { h, m });
  return t('alerts.duration.minutes', { m });
}

// Hälytyksen tila (#31): active = vika yhä päällä, resolved = palautunut,
// unknown = vanha kuitattu hälytys, jonka päättymisaikaa ei tallennettu.
function alertState(a) {
  if (a.normal_since) return 'resolved';
  if (a.end_unknown) return 'unknown';
  return 'active';
}

// ─── NoteField — hälytyksen vapaa muistiinpano (PoC 6, 2.9.2026) ──────────────
// Näkyy aina kortin tiedoissa omana rivinään: teksti tai "—" (2.11.2026).
// Vain Admin kirjoittaa/muokkaa (canEdit). Muokkaustila (editing, value) on
// AlertCardissa, jotta kuittaus voi tallentaa kirjoitetun muistiinpanon ensin:
// 1.11.2026 "Testi" katosi, kun Kuittaa painettiin ennen ✓:ta.

function NoteField({ alert, canEdit, editing, value, setValue, setEditing, saving, error, onSave }) {
  const { t } = useTranslation();

  if (editing) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 w-full">
          <input
            autoFocus
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={t('alerts.notePlaceholder')}
            maxLength={500}
            className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 flex-1 min-w-0 focus:outline-none focus:border-brand-600"
            onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') setEditing(false); }}
          />
          <button onClick={onSave} disabled={saving} className="p-1 text-green-600 hover:text-green-800"><Check size={18} /></button>
          <button onClick={() => setEditing(false)} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {error && <p className="text-xs text-red-700">{t('alerts.noteSaveFailed')}</p>}
      </div>
    );
  }

  return (
    <span className="inline-flex items-start gap-1.5 min-w-0">
      <span className="break-words">{alert.note || '—'}</span>
      {canEdit && (
        alert.note ? (
          <button onClick={() => setEditing(true)} title={t('alerts.editNote')} className="text-gray-400 hover:text-brand-700 shrink-0 mt-0.5">
            <Pencil size={14} />
          </button>
        ) : (
          <button onClick={() => setEditing(true)} className="text-gray-500 hover:text-brand-700 shrink-0">
            {t('alerts.addNote')}
          </button>
        )
      )}
    </span>
  );
}

// ─── AlertCard — yksi hälytys omana korttinaan (#31, 30.9.2026) ───────────────

function DetailRow({ label, children }) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 min-w-0 break-words">{children}</dd>
    </>
  );
}

function StateBadge({ state }) {
  const { t } = useTranslation();
  if (state === 'unknown') return null;
  const cls = state === 'active'
    ? 'bg-red-100 text-red-700 border-red-200'
    : 'bg-green-50 text-green-700 border-green-200';
  return (
    <span className={`shrink-0 text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border ${cls}`}>
      {t(`alerts.state.${state}`)}
    </span>
  );
}

// Viimeisin asiakasilmoitus (#32). Vanhoilla hälytyksillä on vain last_notified_at.
function NotificationInfo({ a, locale, isSuperadmin }) {
  const { t } = useTranslation();
  const recipients = a.last_notification_recipients || [];
  return (
    <div className="space-y-1">
      {a.last_notification_at ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span>{formatTime(a.last_notification_at, locale)}</span>
          {recipients.length > 0 && <span className="text-gray-600 break-all">→ {recipients.join(', ')}</span>}
          {a.last_notification_ok
            ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle size={14} />{t('alerts.notification.sent')}</span>
            : (
              <span className="inline-flex items-center gap-1 text-red-700" title={a.last_notification_error || ''}>
                <XCircle size={14} />
                {recipients.length === 0 ? t('alerts.notification.noRecipients') : t('alerts.notification.failed')}
              </span>
            )}
          {a.notification_count > 1 && (
            <span className="text-gray-500">{t('alerts.notification.count', { count: a.notification_count })}</span>
          )}
        </div>
      ) : a.last_notified_at ? (
        <div>
          {formatTime(a.last_notified_at, locale)}{' '}
          <span className="text-gray-500">{t('alerts.notification.recipientsUnknown')}</span>
        </div>
      ) : '—'}
      {isSuperadmin && a.support_notification_at && (
        <div className="text-gray-600">
          {t(`alerts.notification.support.${a.support_notification_kind}`)} {formatTime(a.support_notification_at, locale)}
          {a.support_notification_ok
            ? <CheckCircle size={14} className="inline ml-1 text-green-700" />
            : <XCircle size={14} className="inline ml-1 text-red-700" />}
        </div>
      )}
    </div>
  );
}

function AlertCard({ a, isSuperadmin, showTenant, canAcknowledge, highlighted, onAck, onSaveNote, cardRef }) {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const state = alertState(a);
  // #64: vian alku (yhteyskatkolle viimeinen heartbeat), vanhoille riveille luontihetki
  const startedAt = a.started_at || a.created_at;
  const startMs = startedAt ? new Date(startedAt).getTime() : null;
  const endMs = a.normal_since ? new Date(a.normal_since).getTime() : null;
  const [acking, setAcking] = useState(false);
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteValue, setNoteValue] = useState(a.note || '');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(false);

  function startNoteEdit(on) {
    if (on) setNoteValue(a.note || '');
    setNoteError(false);
    setNoteEditing(on);
  }

  async function saveNote() {
    setNoteSaving(true);
    setNoteError(false);
    try {
      await onSaveNote(a, noteValue);
      setNoteEditing(false);
      return true;
    } catch {
      setNoteError(true);
      return false;
    } finally {
      setNoteSaving(false);
    }
  }

  async function ack() {
    // Kirjoitettu mutta tallentamaton muistiinpano tallennetaan ennen kuittausta,
    // koska kuittaus siirtää kortin Kuitatut-osioon ja muokkaustila katoaa.
    if (noteEditing && noteValue.trim() !== (a.note || '')) {
      if (!(await saveNote())) return;
    }
    setAcking(true);
    try { await onAck(a); } finally { setAcking(false); }
  }

  return (
    <div
      ref={cardRef}
      className={`bg-white rounded-xl border p-4 sm:p-5 ${highlighted ? 'border-brand-600 ring-2 ring-brand-600/30' : 'border-gray-200'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <AlertTriangle
            size={20}
            className={`mt-0.5 shrink-0 ${state === 'active' ? 'text-red-600' : 'text-gray-400'}`}
          />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">{buildAlertMessage(a, t)}</h3>
        </div>
        <StateBadge state={state} />
      </div>

      <div className="mt-3 pl-0 sm:pl-[30px]">
        {showTenant && a.tenant_name && (
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{a.tenant_name}</p>
        )}
        <Link to={`/suojatila/${a.shelter_id}`} className="text-base font-medium text-brand-700 hover:underline">
          {a.shelter_name}
        </Link>
        {a.shelter_location && (
          <p className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
            <MapPin size={14} className="shrink-0 text-gray-400" />{a.shelter_location}
          </p>
        )}

        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm mt-3">
          <DetailRow label={t('alerts.detail.started')}>{formatTime(startedAt, locale)}</DetailRow>
          {state === 'resolved' && (
            <DetailRow label={t('alerts.detail.ended')}>{formatTime(a.normal_since, locale)}</DetailRow>
          )}
          <DetailRow label={t('alerts.detail.duration')}>
            {state === 'unknown' || startMs == null
              ? <span className="text-gray-500">{t('alerts.detail.endUnknown')}</span>
              : state === 'active'
                ? <>{formatDuration(Date.now() - startMs, t)} <span className="text-red-700">({t('alerts.detail.ongoing')})</span></>
                : formatDuration(endMs - startMs, t)}
          </DetailRow>
          <DetailRow label={t('alerts.detail.notification')}>
            <NotificationInfo a={a} locale={locale} isSuperadmin={isSuperadmin} />
          </DetailRow>
          {a.acknowledged_at && (
            <DetailRow label={t('alerts.detail.acknowledged')}>
              {formatTime(a.acknowledged_at, locale)}
              {a.acknowledged_by_email && <span className="text-gray-600 break-all"> · {a.acknowledged_by_email}</span>}
            </DetailRow>
          )}
          <DetailRow label={t('alerts.detail.note')}>
            <NoteField
              alert={a}
              canEdit={canAcknowledge}
              editing={noteEditing}
              value={noteValue}
              setValue={setNoteValue}
              setEditing={startNoteEdit}
              saving={noteSaving}
              error={noteError}
              onSave={saveNote}
            />
          </DetailRow>
        </dl>
      </div>

      {!a.acknowledged_at && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-end gap-3">
          {canAcknowledge ? (
            <button
              onClick={ack}
              disabled={acking}
              className="shrink-0 inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              <CheckCircle size={16} />
              {t('alerts.acknowledge')}
            </button>
          ) : (
            <span className="shrink-0 text-sm text-gray-500">{t('alerts.notAcknowledgedYet')}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Aktiiviset hälytykset ────────────────────────────────────────────────────

function ActiveTab({ isSuperadmin, showTenant, canAcknowledge }) {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Varoitukset-suodatin poistettu 25.9.2026: PoC:ssa ei synny warning-tason
  // hälytyksiä (ovivalvonta pois käytöstä), joten suodatin näytti aina tyhjää.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/alerts');
      setAlerts(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  async function ack(alert) {
    await api.post(`/api/shelters/${alert.shelter_id}/alerts/${alert.id}/ack`);
    load();
  }

  // Sähköpostin linkki /alerts?alert=<id> (#32): kortti korostetaan ja vieritetään
  // näkyviin kerran ensimmäisen latauksen jälkeen.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('alert');
  const highlightRef = useRef(null);
  const scrolledRef = useRef(false);
  // Kuitatut-osio on oletuksena kiinni. Se avataan, jos linkin hälytys on kuitattu.
  const [ackedOpen, setAckedOpen] = useState(false);
  useEffect(() => {
    if (highlightId && alerts.some(a => a.id === highlightId && a.acknowledged_at)) setAckedOpen(true);
  }, [highlightId, alerts]);

  useEffect(() => {
    if (!loading && highlightRef.current && !scrolledRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      scrolledRef.current = true;
    }
  }, [loading, alerts, ackedOpen]);

  async function saveNote(alert, note) {
    await api.patch(`/api/shelters/${alert.shelter_id}/alerts/${alert.id}/note`, { note });
    load();
  }

  const SEVEN_DAYS_MS = 7 * 24 * 3600 * 1000;
  const counts = {
    unacked: alerts.filter(a => !a.acknowledged_at).length,
    today:   alerts.filter(a =>
      new Date(a.created_at).toDateString() === new Date().toDateString()
    ).length,
    // Tällä viikolla — liukuva 7 vrk ikkuna (yksinkertaisempi ja yksiselitteisempi
    // kuin kalenteriviikko, joka vaatisi viikon alkupäivän/aikavyöhykkeen valinnan).
    week: alerts.filter(a =>
      Date.now() - new Date(a.created_at).getTime() <= SEVEN_DAYS_MS
    ).length,
  };

  const unackedAlerts = alerts.filter(a => !a.acknowledged_at);
  const ackedAlerts = alerts.filter(a => a.acknowledged_at);

  function renderCard(a) {
    const highlighted = a.id === highlightId;
    return (
      <AlertCard
        key={a.id}
        a={a}
        isSuperadmin={isSuperadmin}
        showTenant={showTenant}
        canAcknowledge={canAcknowledge}
        highlighted={highlighted}
        cardRef={highlighted ? highlightRef : undefined}
        onAck={ack}
        onSaveNote={saveNote}
      />
    );
  }

  function renderSection(titleKey, list) {
    return (
      <section>
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
          {t(titleKey)} {list.length > 0 && `(${list.length})`}
        </h2>
        {list.length === 0
          ? <div className="bg-white rounded-xl border border-gray-200 py-8 text-center text-gray-500 text-sm">{t('alerts.noAlerts')}</div>
          : <div className="space-y-3">{list.map(renderCard)}</div>
        }
      </section>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
        <div className="bg-red-50 border border-red-200 rounded-xl px-1.5 py-2.5 sm:p-3 text-center">
          <div className="text-2xl font-bold text-red-700">{counts.unacked}</div>
          <div className="text-xs sm:text-sm text-red-700 mt-0.5 leading-tight break-words">{t('alerts.unackedStat')}</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-1.5 py-2.5 sm:p-3 text-center">
          <div className="text-2xl font-bold text-yellow-700">{counts.today}</div>
          <div className="text-xs sm:text-sm text-yellow-700 mt-0.5 leading-tight">{t('alerts.todayLabel')}</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-1.5 py-2.5 sm:p-3 text-center">
          <div className="text-2xl font-bold text-yellow-700">{counts.week}</div>
          <div className="text-xs sm:text-sm text-yellow-700 mt-0.5 leading-tight">{t('alerts.weekLabel')}</div>
        </div>
      </div>

      {loading
        ? <div className="text-center py-16 text-gray-400 text-sm">{t('alerts.loading')}</div>
        : (
          <div className="space-y-8">
            {renderSection('alerts.unackedSection', unackedAlerts)}

            {/* Kuitatut avattavan otsikon alla, oletuksena kiinni (30.9.2026) */}
            <section>
              <button
                type="button"
                onClick={() => setAckedOpen(o => !o)}
                aria-expanded={ackedOpen}
                className="w-full flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3 text-left hover:bg-gray-50"
              >
                <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  {t('alerts.ackedSection')} ({ackedAlerts.length})
                </span>
                <ChevronDown size={18} className={`text-gray-500 shrink-0 transition-transform ${ackedOpen ? 'rotate-180' : ''}`} />
              </button>
              {ackedOpen && (
                <div className="mt-3">
                  {ackedAlerts.length === 0
                    ? <div className="bg-white rounded-xl border border-gray-200 py-8 text-center text-gray-500 text-sm">{t('alerts.noAlerts')}</div>
                    : <div className="space-y-3">{ackedAlerts.map(renderCard)}</div>
                  }
                </div>
              )}
            </section>
          </div>
        )
      }
    </div>
  );
}

// ─── AlertsPage ───────────────────────────────────────────────────────────────

export default function AlertsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isSuperadmin = profile?.role === 'superadmin';
  // Huolto (#86) näkee usean asiakkaan hälytyksiä, joten asiakkaan nimi näytetään.
  const showTenant = isSuperadmin || profile?.role === 'huolto';
  // Vain Admin kuittaa hälytyksiä ja kirjoittaa muistiinpanoja — ei Superadmin
  // (PoC 6, 2.9.2026: kuittaus/muistiinpano on asiakkaan oma toimenpide).
  const canAcknowledge = profile?.role === 'admin';

  return (
    <div>
      <h1 className="text-xl font-bold text-gray-900 mb-4">{t('alerts.title')}</h1>
      <ActiveTab isSuperadmin={isSuperadmin} showTenant={showTenant} canAcknowledge={canAcknowledge} />
    </div>
  );
}
